/**
 * Liste over WorkActivity-records (pause, værksted, vask, andet) logget af
 * chauffør-app via X-API-Key. Genbruger DateRangePicker + samme stil som
 * WorkHoursList for konsistens.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Box, Stack, Typography, Paper, Table, TableBody, TableCell, TableHead,
  TableRow, Chip, FormControl, InputLabel, Select, MenuItem, IconButton,
  CircularProgress, Tooltip, Alert,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { api } from '../../services/api';
import { WorkActivityEntry, WorkActivityKind, DateRange } from '../../types';
import DateRangePicker, {
  getDefaultDateRange,
  formatDateRange,
} from '../../components/common/DateRangePicker';

const KIND_LABEL: Record<WorkActivityKind, string> = {
  BREAK: 'Pause',
  WORKSHOP: 'Værksted',
  WASHING: 'Vask',
  OTHER: 'Andet',
};

const KIND_COLOR: Record<WorkActivityKind, 'default' | 'warning' | 'info' | 'success'> = {
  BREAK: 'default',
  WORKSHOP: 'warning',
  WASHING: 'info',
  OTHER: 'success',
};

function formatHm(minutes: number | null): string {
  if (minutes == null) return '—';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h}t ${m}m` : `${m}m`;
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function WorkActivitiesList() {
  const [dateRange, setDateRange] = useState<DateRange>(getDefaultDateRange());
  const { from, to } = formatDateRange(dateRange);
  const [driverId, setDriverId] = useState<string>('');
  const [kind, setKind] = useState<string>('');
  const [entries, setEntries] = useState<WorkActivityEntry[]>([]);
  const [drivers, setDrivers] = useState<Array<{ id: string; name: string }>>([]);
  const [totals, setTotals] = useState<{ byKind: Record<string, number>; minutes: number }>({
    byKind: {},
    minutes: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const filters: any = {};
      if (driverId) filters.driverId = driverId;
      if (kind) filters.kind = kind;
      const res = await api.getWorkActivities(from, to, filters);
      setEntries(res.entries);
      setDrivers(res.drivers);
      setTotals(res.totals);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Kunne ikke hente aktiviteter');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [from, to, driverId, kind]);

  const totalChips = useMemo(() => {
    return Object.entries(totals.byKind)
      .filter(([, m]) => m > 0)
      .map(([k, m]) => ({ kind: k as WorkActivityKind, minutes: m }));
  }, [totals]);

  const handleDelete = async (id: string) => {
    if (!confirm('Slet aktivitet?')) return;
    await api.deleteWorkActivity(id);
    load();
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start" sx={{ mb: 2 }}>
        <DateRangePicker value={dateRange} onChange={setDateRange} />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Chauffør</InputLabel>
          <Select
            value={driverId}
            label="Chauffør"
            onChange={(e) => setDriverId(e.target.value)}
          >
            <MenuItem value="">Alle</MenuItem>
            {drivers.map((d) => (
              <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Kategori</InputLabel>
          <Select
            value={kind}
            label="Kategori"
            onChange={(e) => setKind(e.target.value)}
          >
            <MenuItem value="">Alle</MenuItem>
            {(Object.keys(KIND_LABEL) as WorkActivityKind[]).map((k) => (
              <MenuItem key={k} value={k}>{KIND_LABEL[k]}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Total i perioden
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <Typography variant="h5">{formatHm(totals.minutes)}</Typography>
          {totalChips.map(({ kind, minutes }) => (
            <Chip
              key={kind}
              label={`${KIND_LABEL[kind]}: ${formatHm(minutes)}`}
              color={KIND_COLOR[kind]}
              size="small"
            />
          ))}
          {totalChips.length === 0 && (
            <Typography variant="body2" color="text.secondary">Ingen aktiviteter i perioden</Typography>
          )}
        </Stack>
      </Paper>

      <Paper>
        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress />
          </Box>
        ) : entries.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Ingen aktiviteter — chaufførerne har endnu ikke logget pause/værksted/vask i appen.
            </Typography>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Dato</TableCell>
                <TableCell>Chauffør</TableCell>
                <TableCell>Kategori</TableCell>
                <TableCell>Start</TableCell>
                <TableCell>Slut</TableCell>
                <TableCell align="right">Varighed</TableCell>
                <TableCell>Trailer</TableCell>
                <TableCell>Note</TableCell>
                <TableCell align="right" sx={{ width: 80 }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map((e) => (
                <TableRow key={e.id} hover>
                  <TableCell>{e.date}</TableCell>
                  <TableCell>{e.driverName}</TableCell>
                  <TableCell>
                    <Chip
                      label={
                        e.kind === 'OTHER'
                          ? (e.customLabel ? `Andet · ${e.customLabel}` : 'Andet')
                          : KIND_LABEL[e.kind]
                      }
                      color={KIND_COLOR[e.kind]}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatDateTime(e.startedAt)}</TableCell>
                  <TableCell>
                    {e.endedAt ? formatDateTime(e.endedAt) : <Chip label="Løbende" size="small" color="primary" />}
                  </TableCell>
                  <TableCell align="right">{formatHm(e.minutes)}</TableCell>
                  <TableCell>{e.trailerNumber ? `#${e.trailerNumber}` : '—'}</TableCell>
                  <TableCell>
                    <Tooltip title={e.notes || ''}>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                        {e.notes || '—'}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleDelete(e.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>
    </Box>
  );
}
